import React, { FC, ReactElement, useEffect, useRef } from "react";
import { Paper } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import classnames from "classnames";

import { useChatMessagesStyles } from "./ChatMessagesStyles";
import { useGlobalStyles } from "../../../util/globalClasses";
import { selectChatMessagesItems, selectIsChatMessagesLoading } from "../../../store/ducks/chatMessages/selectors";
import { selectUserDataId } from "../../../store/ducks/user/selectors";
import Spinner from "../../../components/Spinner/Spinner";
import EmptyChatMessages from "./EmptyChatMessages/EmptyChatMesseges";
import ChatHeader from "./ChatHeader/ChatHeader";
import ChatMessage from "./ChatMessage/ChatMessage";
import ChatUserRestriction from "./ChatUserRestriction/ChatUserRestriction";
import ChatFooter from "./ChatFooter/ChatFooter";
import { fetchChatMessages, resetChatMessages, setChatMessagesLoadingState } from "../../../store/ducks/chatMessages/actionCreators";
import { fetchReadMessages } from "../../../store/ducks/user/actionCreators";
import { fetchChatParticipant } from "../../../store/ducks/userProfile/actionCreators";
import { selectUserProfile } from "../../../store/ducks/userProfile/selectors";
import { fetchChat } from "../../../store/ducks/chat/actionCreators";
import { CONVERSATION_INFO } from '../../../constants/common-constants';
import { LoadingStatus } from "../../../types/common";
import { selectChatsItems } from '../../../store/ducks/chats/selectors';

interface ChatMessagesProps {
    participantId?: string;
    chatId?: string;
}

const ChatMessages: FC<ChatMessagesProps> = ({ participantId, chatId }): ReactElement => {
    const globalClasses = useGlobalStyles({});
    const classes = useChatMessagesStyles();
    const dispatch = useDispatch();
    const myProfileId = useSelector(selectUserDataId);
    const chatParticipant = useSelector(selectUserProfile);
    const messages = useSelector(selectChatMessagesItems);
    const chats = useSelector(selectChatsItems);
    const isChatMessagesLoading = useSelector(selectIsChatMessagesLoading);
    const chatEndRef = useRef<HTMLDivElement>(null);

    const [isUserDeleted, setIsUserDeleted] = React.useState(false);
    const [userName, setUserName] = React.useState('');

    const handleChatMessages = (chatId: string, participantId: string): void => {
        dispatch(fetchChat(chatId));
        dispatch(fetchChatParticipant({ participantId, chatId }));
        dispatch(setChatMessagesLoadingState(LoadingStatus.LOADING));
        dispatch(fetchChatMessages(chatId));
        dispatch(fetchReadMessages(chatId));
        localStorage.removeItem(CONVERSATION_INFO);
    }

    useEffect(() => {
        if (chatId && participantId) {
            handleChatMessages(chatId, participantId);
            const getSelectedChat = chats.find((chat) => chat.id === chatId);
            if (getSelectedChat?.participants) {
                const participant = getSelectedChat?.participants.find((participant) => participant.user.id == participantId);
                if (participant?.user.deletionRequested) {
                    setUserName(participant.user.username);
                    setIsUserDeleted(true);
                }
            }
        }

        return () => {
            dispatch(resetChatMessages());
        };
    }, [chatId, participantId]);

    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    return (
        <Paper className={classnames(globalClasses.pageContainer, classes.chatContainer)} variant="outlined">
            {(!participantId) ? (
                <EmptyChatMessages />
            ) : (
                <>
                    <ChatHeader />
                    <Paper className={classes.chat}>
                        {isChatMessagesLoading ? (
                            <Spinner paddingTop={150} />
                        ) : (
                            <>
                                {messages.map((message) => (
                                    <ChatMessage
                                        key={message.id}
                                        message={message}
                                        isParticipantMessage={message.author.id !== myProfileId}
                                    />
                                ))}
                                <div ref={chatEndRef} />
                            </>
                        )}
                    </Paper>
                    <>
                        {(chatParticipant?.isUserBlocked || chatParticipant?.isMyProfileBlocked) ? (
                            <ChatUserRestriction />
                        ) : isUserDeleted ? <ChatUserRestriction userName={userName} isDeleted /> : (
                            <ChatFooter chatId={chatId!} />
                        )}
                    </>
                </>
            )}
        </Paper>
    );
};

export default ChatMessages;
